import { notify } from '@/components/core';
import React, { useState } from 'react';
import { X, Calculator, Plus, Save, Box, BarChart2 } from 'lucide-react';

interface MeasureWizardModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (measure: any) => void;
    onDeleteMeasure?: (measure: any) => void;  // NEW: Optional delete callback
    existingMeasures: any[];
    availableFields?: {
        dimensions: any[];
        measures: any[];
    };
    initialData?: any; // New Prop for Editing
}

export const MeasureWizardModal: React.FC<MeasureWizardModalProps> = ({
    isOpen,
    onClose,
    onSave,
    onDeleteMeasure,
    existingMeasures,
    availableFields = { dimensions: [], measures: [] },
    initialData = null
}) => {
    // Initialize state from initialData if present
    const [name, setName] = useState(initialData?.name || '');
    const [description, setDescription] = useState(initialData?.description || '');
    const [formula, setFormula] = useState(initialData?.formula || '');
    const [format, setFormat] = useState(initialData?.format || 'number');
    const [decimals, setDecimals] = useState(initialData?.decimals ?? 2);

    // Reset or Load when opening
    React.useEffect(() => {
        if (isOpen) {
            setName(initialData?.name || '');
            setDescription(initialData?.description || '');
            setFormula(initialData?.formula || '');
            setFormat(initialData?.format || 'number');
            setDecimals(initialData?.decimals ?? 2);
        }
    }, [isOpen, initialData]);

    // Autocomplete State
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [filteredSuggestions, setFilteredSuggestions] = useState<any[]>([]);
    const [cursorPosition, setCursorPosition] = useState(0);
    const [activeSearchTerm, setActiveSearchTerm] = useState(''); // The word being replaced
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    const [selectedIndex, setSelectedIndex] = useState(0);

    const supportedFunctions = [
        // Aggregations
        { name: 'SUM', description: 'Calculates the sum of a column', type: 'function' },
        { name: 'AVG', description: 'Calculates the average of a column', type: 'function' },
        { name: 'COUNT', description: 'Counts the number of rows', type: 'function' },
        { name: 'DISTINCTCOUNT', description: 'Counts unique values', type: 'function' },
        { name: 'MIN', description: 'Returns the smallest value', type: 'function' },
        { name: 'MAX', description: 'Returns the largest value', type: 'function' },
        { name: 'DIVIDE', description: 'Safe division function', type: 'function' },

        // Filter Context
        { name: 'CALCULATE', description: 'Evaluates expression in a modified filter context', type: 'function' },
        { name: 'FILTER', description: 'Returns a table that represents a subset of another table', type: 'function' },
        { name: 'ALL', description: 'Returns all rows/values, ignoring any filters', type: 'function' },
        { name: 'ALLEXCEPT', description: 'Removes all context filters except specified columns', type: 'function' },

        // Time Intelligence
        { name: 'DATEADD', description: 'Moves the given set of dates by a specified interval', type: 'function' },
        { name: 'SAMEPERIODLASTYEAR', description: 'Returns dates from the previous year', type: 'function' },
        { name: 'DATESYTD', description: 'Returns a set of dates for the year-to-date', type: 'function' },
        { name: 'TOTALYTD', description: 'Evaluates expression for the year-to-date', type: 'function' },
        { name: 'TOTALQTD', description: 'Evaluates expression for the quarter-to-date', type: 'function' },
        { name: 'TOTALMTD', description: 'Evaluates expression for the month-to-date', type: 'function' },
    ];

    // Filter fields based on search (very simple for now)
    const allFields = [
        ...availableFields.measures.map(m => ({ ...m, type: 'measure' })),
        ...availableFields.dimensions.map(d => ({ ...d, type: 'dimension' }))
    ];

    if (!isOpen) return null;

    const handleSave = () => {
        if (!name || !formula) {
            notify.warning("Name and Formula are required.");
            return;
        }

        const newMeasure = {
            id: initialData?.id || `measure_${Date.now()}`,
            name,
            description,
            formula,
            format,
            decimals,
            isCustom: true,
            type: 'number' // Measures are almost always numbers
        };

        onSave(newMeasure);
        onClose();
        // Reset form
        setName('');
        setDescription('');
        setFormula('');
        setFormat('number');
    };

    const insertField = (fieldName: string) => {
        setFormula(prev => prev + `[${fieldName}] `);
        if (textareaRef.current) textareaRef.current.focus();
    };

    const insertSuggestion = (item: any) => {
        const itemText = item.type === 'function' ? `${item.name}(` : `[${item.name}]`;

        // Replace the "activeSearchTerm" before the cursor with the new itemText
        const beforeCursor = formula.substring(0, cursorPosition);
        const afterCursor = formula.substring(cursorPosition);

        // Find where the search term started
        // If we matched on '(', or '[', we replace just that char? 
        // Actually, our logic captures the "word" ending at cursor.

        const termLength = activeSearchTerm.length;
        const newBefore = beforeCursor.slice(0, -termLength) + itemText;

        setFormula(newBefore + afterCursor);
        setShowSuggestions(false);
        setActiveSearchTerm('');

        setTimeout(() => {
            if (textareaRef.current) {
                textareaRef.current.focus();
                const newPos = newBefore.length;
                textareaRef.current.setSelectionRange(newPos, newPos);
            }
        }, 0);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (showSuggestions) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedIndex(prev => (prev + 1) % filteredSuggestions.length);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIndex(prev => (prev - 1 + filteredSuggestions.length) % filteredSuggestions.length);
            } else if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();
                if (filteredSuggestions[selectedIndex]) {
                    insertSuggestion(filteredSuggestions[selectedIndex]);
                }
            } else if (e.key === 'Escape') {
                e.preventDefault();
                setShowSuggestions(false);
            }
        }
    };

    const handleFormulaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const val = e.target.value;
        const pos = e.target.selectionStart;
        setFormula(val);
        setCursorPosition(pos);

        // Logic to detect "Current Word" or "Trigger Context"
        const textBeforeCursor = val.slice(0, pos);

        // Regex to find the last "token" before cursor
        // Matches:
        // 1. Alphanumeric words (potentially with underscores) at end of string: /[a-zA-Z0-9_]+$/
        // 2. A explicit trigger like '[' at end of string
        // 3. A function open parenthesis like '(' - though usually we want to see fields immediately after.

        // Case A: Explicit Field Trigger '['
        if (textBeforeCursor.endsWith('[')) {
            setActiveSearchTerm('[');
            setFilteredSuggestions(allFields);
            setShowSuggestions(true);
            setSelectedIndex(0);
            return;
        }

        // Case B: Function Parameter Trigger '(' or ', '
        // Logic: specific operators typically precede a field search
        if (/*textBeforeCursor.endsWith('(') ||*/ textBeforeCursor.endsWith(', ')) {
            // NOTE: user wanted "sum(d" -> suggest functions? No, that's inside a function.
            // Wait, images show:
            // "sum(d" -> suggests "DISTINCTCOUNT" (nested function) or "DAY"
            // "sum(tri" -> suggests "trip_distance"

            // So simpler approach: Just look at the word boundary.
        }

        // GENERAL "Typeahead" approach:
        // Find the last "word" segment.
        const match = textBeforeCursor.match(/([a-zA-Z0-9_]+)$/);

        if (match) {
            const word = match[1];
            setActiveSearchTerm(word);

            // Check context: are we possibly typing a function or a field?
            // If we are strictly "fresh" (start of line or after space), functions are likely.
            // If we are inside `sum(`, fields are likely.
            // Implem: Merged list usually works best.

            // Filter
            const matchedFuncs = supportedFunctions.filter(f => f.name.toLowerCase().startsWith(word.toLowerCase()));
            const matchedFields = allFields.filter(f => f.name.toLowerCase().includes(word.toLowerCase()));

            const combined = [...matchedFuncs, ...matchedFields];

            if (combined.length > 0) {
                setFilteredSuggestions(combined);
                setShowSuggestions(true);
                setSelectedIndex(0);
            } else {
                setShowSuggestions(false);
            }
        } else {
            // No word found. Check for pure triggers like '(' or space?
            // If just typed '(', show all fields?
            if (textBeforeCursor.endsWith('(')) {
                // Suggest all fields and functions (since functions can be nested)
                setActiveSearchTerm(''); // searching nothing, just showing list
                const combined = [...supportedFunctions, ...allFields];
                setFilteredSuggestions(combined);
                setShowSuggestions(true);
                setSelectedIndex(0);
            } else {
                setShowSuggestions(false);
            }
        }
    };

    // Suggestion List Component
    const SuggestionsList = () => {
        if (!showSuggestions || filteredSuggestions.length === 0) return null;

        return (
            <div className="absolute left-4 bottom-16 z-50 w-72 max-h-60 overflow-y-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-xl font-sans animate-in fade-in zoom-in-95 duration-100 flex flex-col">
                <div className="p-2 text-[10px] font-bold text-slate-400 bg-slate-50 dark:bg-slate-900 border-b border-slate-100 dark:border-slate-700 sticky top-0 flex justify-between">
                    <span>SUGGESTIONS</span>
                    <span className="font-normal text-xs text-slate-400">tab to select</span>
                </div>
                {filteredSuggestions.map((item, idx) => (
                    <button
                        key={idx}
                        onClick={() => insertSuggestion(item)}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-xs text-left transition-colors border-b border-slate-50 dark:border-slate-700/50 last:border-0 ${idx === selectedIndex
                            ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-100'
                            : 'hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200'
                            }`}
                        onMouseEnter={() => setSelectedIndex(idx)}
                    >
                        {/* Icon based on type */}
                        <div className="shrink-0">
                            {item.type === 'function' && <div className="w-5 h-5 flex items-center justify-center bg-purple-100 dark:bg-purple-900/50 rounded text-purple-600 font-serif font-bold italic">fx</div>}
                            {item.type === 'measure' && <BarChart2 className="w-4 h-4 text-emerald-500" />}
                            {item.type === 'dimension' && <Box className="w-4 h-4 text-blue-500" />}
                        </div>

                        <div className="flex flex-col overflow-hidden">
                            <span className="truncate font-medium">{item.name}</span>
                            {/* Show description for functions */}
                            {item.description && <span className="text-[10px] text-slate-400 truncate">{item.description}</span>}
                        </div>
                    </button>
                ))}
            </div>
        );
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-4xl overflow-hidden border border-slate-200 dark:border-slate-700 flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg">
                            <Calculator className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">
                                {initialData ? 'Edit Measure' : 'New Measure'}
                            </h2>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                {initialData ? 'Update your custom calculation' : 'Define a custom calculation for your dashboard'}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors">
                        <X className="w-5 h-5 text-slate-500" />
                    </button>
                </div>

                {/* Body - Scrollable */}
                <div className="flex-1 overflow-auto p-6 space-y-6">
                    {/* Name & Description */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Name</label>
                            <input
                                type="text"
                                className="w-full p-2.5 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                placeholder="e.g. Net Profit"
                                value={name}
                                onChange={e => setName(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Description</label>
                            <input
                                type="text"
                                className="w-full p-2.5 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                placeholder="Optional description..."
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Formula Editor Area */}
                    <div className="flex gap-4 h-[300px]">
                        {/* Left: Editor */}
                        <div className="flex-1 flex flex-col space-y-2 relative">
                            <label className="flex items-center justify-between text-xs font-bold text-slate-500 uppercase tracking-wider">
                                <span>Formula (Expression Language (AEL))</span>
                                <span className="text-[10px] font-normal text-slate-400">Supported: +, -, *, /, SUM, AVG</span>
                            </label>
                            <div className="relative flex-1 group">
                                <textarea
                                    ref={textareaRef}
                                    className="w-full h-full p-4 font-mono text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-900 focus:ring-2 focus:ring-blue-500 outline-none resize-none leading-relaxed"
                                    placeholder="SUM(Sales) - SUM(Cost)"
                                    value={formula}
                                    onChange={handleFormulaChange}
                                    onKeyDown={handleKeyDown}
                                    onClick={() => setShowSuggestions(false)}
                                    spellCheck={false}
                                />
                                <SuggestionsList />
                                <div className="absolute bottom-3 right-3 text-[10px] text-slate-400 bg-white dark:bg-slate-800 px-2 py-1 rounded border border-slate-200 dark:border-slate-600 shadow-sm pointer-events-none">
                                    Type '(' or '[' to see fields
                                </div>
                            </div>
                        </div>

                        {/* Right: Field List */}
                        <div className="w-1/3 flex flex-col space-y-2">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Available Fields</label>
                            <div className="flex-1 border border-slate-200 dark:border-slate-600 rounded-lg overflow-hidden flex flex-col bg-slate-50 dark:bg-slate-900/50">
                                <div className="flex-1 overflow-y-auto p-2 space-y-4">

                                    {/* Measures Section */}
                                    {availableFields.measures.length > 0 && (
                                        <div>
                                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 px-2">Measures</div>
                                            <div className="space-y-1">
                                                {availableFields.measures.map((m: any, idx: number) => (
                                                    <button
                                                        key={`m-${idx}`}
                                                        onClick={() => insertField(m.name)}
                                                        className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-slate-700 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800 hover:shadow-sm rounded transition-all text-left group"
                                                    >
                                                        <BarChart2 className="w-3 h-3 text-emerald-500" />
                                                        <span className="truncate flex-1 font-medium">{m.name}</span>
                                                        <Plus className="w-3 h-3 opacity-0 group-hover:opacity-100 text-slate-400" />
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Dimensions Section */}
                                    {availableFields.dimensions.length > 0 && (
                                        <div>
                                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 mt-2 px-2">Dimensions</div>
                                            <div className="space-y-1">
                                                {availableFields.dimensions.map((d: any, idx: number) => (
                                                    <button
                                                        key={`d-${idx}`}
                                                        onClick={() => insertField(d.name)}
                                                        className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-slate-700 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800 hover:shadow-sm rounded transition-all text-left group"
                                                    >
                                                        <Box className="w-3 h-3 text-blue-500" />
                                                        <span className="truncate flex-1">{d.name}</span>
                                                        <Plus className="w-3 h-3 opacity-0 group-hover:opacity-100 text-slate-400" />
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {availableFields.measures.length === 0 && availableFields.dimensions.length === 0 && (
                                        <div className="text-center text-xs text-slate-400 py-4 italic">
                                            No fields available. Select a dataset first.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Formatting Removed per User Request */}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-700 shrink-0">
                    <div>
                        {initialData && onDeleteMeasure && (
                            <button
                                onClick={() => {
                                    onDeleteMeasure(initialData);
                                    onClose();
                                }}
                                className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                            >
                                Delete Measure
                            </button>
                        )}
                    </div>

                    <div className="flex items-center gap-3">
                        <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 dark:text-slate-400 focus:outline-none">
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                        >
                            <Save className="w-4 h-4" />
                            {initialData ? 'Update Measure' : 'Create Measure'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};